<template>
  <el-row class="d-flex">
    <el-button
      class="ml-auto mr-1"
      type="primary"
      :disabled="!userCanWriteEngagementGainImportConfiguration || isSignedOff"
      @click="showNewConfigurationModal = true"
    >
      <h4>{{ $t('add') }}</h4>
    </el-button>
  </el-row>
  <el-row
    v-for="(item, index) in midBatchGainApiFileConfigurations"
    :key="index"
    class="mt-1"
  >
    <mid-batch-api-file-configuration-card
      :configuration="item"
      :configuration-history="gainApiFileConfigurationHistory ?? []"
      :disable-delete-and-save="
        !userCanWriteEngagementGainImportConfiguration || isSignedOff
      "
      :api-connections="gainApiConnectionConfigurations"
      :exiting-output-file-names="existingFileNames(index)"
      :sql-nodes="sqlNodes"
      @update="(configuration: ApiFileConfiguration) => updateEntry(index, configuration)"
      @delete="deleteEntry(index)"
      @save="saveEntry(index)"
      @fetch-history="fetchApiFileConfigurationHistory(item.configurationName!)"
    />
  </el-row>
  <new-api-file-configuration-modal
    :model-value="showNewConfigurationModal"
    :existing-names="
      allGainApiFileConfigurations.map(el => el.configurationName ?? '')
    "
    @close="showNewConfigurationModal = false"
    @confirm="handleConfigurationCreate"
  />
  <base-confirmation-modal
    v-model="showDeleteConfigurationModal"
    :feedback-text="$t('configurationDeleted')"
    @confirm="deleteConfiguration"
    @close="
      {
        configurationNameToBeDeleted = null;
        showDeleteConfigurationModal = false;
      }
    "
  >
    <template #content>
      <p>{{ $t('confirmDeleteConfiguration') }}</p>
      <p class="mt-2">
        <strong>{{ $t('confirmDeleteConfigurationNote') }}</strong>
      </p>
    </template>
  </base-confirmation-modal>
</template>

<script lang="ts">
import { ElRow, ElButton, ElMessage } from 'element-plus';
import { useStore } from 'vuex';
import { computed, ComputedRef, onActivated, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import i18n from '@/i18n';
import {
  RequirementsStatus,
  ApiFileConfiguration,
  ApiConnectionConfiguration,
  ProcessType,
  ExecutionTrigger,
  ApiName
} from '@etp/etp-gain-bff-client/axios';
import { NodeModel } from '@etp/etp-nodemodelgit-client/axios';
import MidBatchApiFileConfigurationCard from './components/MidBatchApiFileConfigurationCard.vue';
import NewApiFileConfigurationModal from '@/views/home/data-journey/import/components/NewApiFileConfigurationModal.vue';
import BaseConfirmationModal from '@/components/modals/BaseConfirmationModal.vue';

export default {
  name: 'GainMidBatchApiImportFiles',
  components: {
    ElRow,
    ElButton,
    MidBatchApiFileConfigurationCard,
    NewApiFileConfigurationModal,
    BaseConfirmationModal
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    const showVersionModal = ref(false);
    const showNewConfigurationModal = ref(false);
    const showDeleteConfigurationModal = ref(false);
    const configurationNameToBeDeleted = ref<string | undefined | null>(null);

    const gainApiConnectionConfigurations: ComputedRef<
      Array<ApiConnectionConfiguration>
    > = computed(
      () => store.getters['engagements/getApiConnectionConfigurations']
    );

    const gainConfiguration = computed(
      () => store.getters['engagements/getGainConfiguration']
    );

    const sqlNodes = computed<Array<NodeModel>>(
      () => store.getters['filtering/configNodes'] ?? []
    );

    const isGainEngagement: ComputedRef<boolean> = computed(
      () => store.getters['home/isGainEngagement']
    );

    const isSignedOff: ComputedRef<boolean> = computed(
      () =>
        store.getters['gain/getCurrentRequirementsStatus']
          ?.requirementsStatus === RequirementsStatus.SignedOff
    );

    const midBatchGainApiFileConfigurations = ref(
      new Array<ApiFileConfiguration>()
    );
    const allGainApiFileConfigurations = ref(new Array<ApiFileConfiguration>());
    const gainApiFileConfigurationHistory = ref(
      new Array<ApiFileConfiguration>()
    );

    const existingFileNames = (index: number) => {
      const otherApiNameFileNames = allGainApiFileConfigurations.value
        .filter(
          (config: ApiFileConfiguration) =>
            config.apiName !==
            midBatchGainApiFileConfigurations.value[index].apiName
        )
        .map(
          (config: ApiFileConfiguration) => config.outputFileName
        ) as string[];

      const sameApiNameFileNamesExceptIndex =
        midBatchGainApiFileConfigurations.value
          .filter((config: ApiFileConfiguration, idx: number) => idx !== index)
          .map(
            (config: ApiFileConfiguration) => config.outputFileName
          ) as string[];

      return [...otherApiNameFileNames, ...sameApiNameFileNamesExceptIndex];
    };

    const handleConfigurationCreate = (name: string) => {
      midBatchGainApiFileConfigurations.value.push({
        configurationName: name,
        apiName: ApiName.Haliburton,
        executionTrigger: ExecutionTrigger.MidBatch
      } as ApiFileConfiguration);
      showNewConfigurationModal.value = false;
    };
    const deleteEntry = (index: number) => {
      const configuration = midBatchGainApiFileConfigurations.value[index];
      if (configuration.version === undefined) {
        midBatchGainApiFileConfigurations.value.splice(index, 1);
      } else {
        showDeleteConfigurationModal.value = true;
        configurationNameToBeDeleted.value = configuration.configurationName;
      }
    };

    const deleteConfiguration = async () => {
      await store.dispatch(
        'import/deleteGainImportApiFileConfiguration',
        configurationNameToBeDeleted.value
      );
      configurationNameToBeDeleted.value = null;
      showDeleteConfigurationModal.value = false;
      await updateLocalVars();
    };
    const updateEntry = (
      index: number,
      configuration: ApiFileConfiguration
    ) => {
      midBatchGainApiFileConfigurations.value[index].outputFileName =
        configuration.outputFileName;
      midBatchGainApiFileConfigurations.value[index].apiName =
        configuration.apiName;
      midBatchGainApiFileConfigurations.value[index].executionTrigger =
        configuration.executionTrigger;
      midBatchGainApiFileConfigurations.value[index].numberOfRetries =
        configuration.numberOfRetries;
      midBatchGainApiFileConfigurations.value[index].retryDelay =
        configuration.retryDelay;
      midBatchGainApiFileConfigurations.value[index].apiSettings =
        configuration.apiSettings;
    };
    const saveEntry = async (index: number) => {
      const configuration = midBatchGainApiFileConfigurations.value[index];
      try {
        await store.dispatch(
          'import/createOrUpdateGainImportApiFileConfiguration',
          configuration
        );
        await updateLocalVars();
        ElMessage.success({
          message: i18n.global.t('configurationSavedSuccessfully'),
          showClose: true
        });
      } catch (error) {
        ElMessage.error({
          message: i18n.global.t('failedToSaveConfiguration'),
          showClose: true
        });
      }
    };

    const fetchApiFileConfigurationHistory = async (
      configurationName: string
    ) => {
      if (configurationName) {
        await store.dispatch(
          'import/fetchGainImportApiFileConfigurationHistory',
          configurationName
        );
        gainApiFileConfigurationHistory.value =
          store.getters['import/getGainImportApiFileConfigurationHistory'];
      }
    };

    const updateLocalVars = async () => {
      await store.dispatch('import/fetchGainImportApiFileConfigurations');
      allGainApiFileConfigurations.value = [
        ...store.getters['import/getGainImportApiFileConfigurations']
      ];
      midBatchGainApiFileConfigurations.value =
        allGainApiFileConfigurations.value.filter(
          (config: ApiFileConfiguration) =>
            config.executionTrigger === ExecutionTrigger.MidBatch
        );
    };
    const userCanWriteEngagementGainImportConfiguration: ComputedRef<boolean> =
      computed(
        () =>
          store.getters[
            'userPermissions/canWriteEngagementGainImportConfiguration'
          ]
      );

    onMounted(async () => {
      if (!isGainEngagement.value) router.push({ name: 'ImportMonitor' });
      const engagement = store.getters['home/getCurrentEngagement'];
      await store.dispatch('gain/fetchCurrentRequirementsStatus');
      await store.dispatch(
        'userPermissions/checkIfCanWriteEngagementGainImportConfiguration'
      );
      await store.dispatch(
        'engagements/fetchApiConnectionConfigurations',
        engagement.id
      );

      await store.dispatch('engagements/fetchGainConfiguration', engagement.id);
      const branchName =
        gainConfiguration.value?.inputFlow_Transformations_MainBranchName;
      if (branchName) {
        store.commit('filtering/SET_SELECTED_CONFIG_BRANCH', {
          name: branchName
        });
        await store.dispatch('filtering/fetchConfigNodes');
      }

      await updateLocalVars();
    });

    onActivated(() => {
      store.dispatch('import/setSelectedProcessType', {
        processType: ProcessType.GainData
      });
    });

    return {
      allGainApiFileConfigurations,
      configurationNameToBeDeleted,
      deleteConfiguration,
      deleteEntry,
      existingFileNames,
      sqlNodes,
      gainApiConnectionConfigurations,
      midBatchGainApiFileConfigurations,
      gainApiFileConfigurationHistory,
      handleConfigurationCreate,
      isSignedOff,
      showDeleteConfigurationModal,
      showNewConfigurationModal,
      showVersionModal,
      updateEntry,
      saveEntry,
      fetchApiFileConfigurationHistory,
      userCanWriteEngagementGainImportConfiguration
    };
  }
};
</script>
